import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import {Link as RouteLink} from 'react-router-dom'
import {useIntl} from 'react-intl'

// Components
import {
    Box,
    Breadcrumb as ChakraBreadcrumb,
    BreadcrumbItem as ChakraBreadcrumbItem,
    BreadcrumbLink as ChakraBreadcrumbLink,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'

// Icons
import {ChevronLeftIcon} from '@salesforce/retail-react-app/app/components/icons'

// Utils
import {categoryUrlBuilder} from '../../utils/url'

const Breadcrumb = ({categories, isPDP = false, showImmediateParentOnMobile = false, variant, ...rest}) => {
    const intl = useIntl()
    const styles = useMultiStyleConfig('Breadcrumb', {variant})

    useEffect(() => {
        const separators = document.querySelectorAll('.sf-breadcrumb [role="presentation"]');

        separators.forEach(separator => {
            separator.setAttribute('aria-hidden', 'true');
        })
    }, [categories])


    // Early return if no categories
    if (!categories?.length) return null

    // Remove the first category (usually root)
    const filteredCategories = categories.slice(1)

    // Determine if we should render the breadcrumb
    const shouldRenderForNonPDP = filteredCategories.length > 0
    const shouldRenderForPDP = filteredCategories.length > 0

    if (!isPDP && !shouldRenderForNonPDP) return null
    if (isPDP && !shouldRenderForPDP) return null

    // Calculate parent category index safely
    const parentIndex = Math.max(0, filteredCategories.length - 2)
    const parentCategory = filteredCategories[parentIndex]

    return (
        <>
            {/* Mobile view (non-PDP): Show only the immediate parent with a chevron icon */}
            {showImmediateParentOnMobile && (
                <ChakraBreadcrumb
                    className="sf-breadcrumb"
                    separator="/"
                    sx={{...styles.container, display: {base: 'flex', lg: 'none'}}}
                    {...rest}
                >
                    <ChakraBreadcrumbItem key={parentCategory?.id} data-testid="sf-crumb-item">
                        <ChakraBreadcrumbLink
                            as={RouteLink}
                            to={categoryUrlBuilder(parentCategory, intl.locale)}
                            sx={styles.link}
                        >
                            <ChevronLeftIcon boxSize="5" aria-hidden="true" />
                            <Box sx={styles.bcText} as={'span'}>
                                {parentCategory?.name}
                            </Box>
                        </ChakraBreadcrumbLink>
                    </ChakraBreadcrumbItem>
                </ChakraBreadcrumb>
            )}

            {/* Desktop view OR PDP (mobile/desktop): Show full breadcrumb */}
            <ChakraBreadcrumb
                className="sf-breadcrumb"
                separator="/"
                sx={{...styles.container, display: isPDP ? 'flex' : {base: showImmediateParentOnMobile ? 'none' : 'flex', lg: 'flex'}}}
                {...rest}
            >
                {filteredCategories.map((category, index) => {
                    const isLast = index === filteredCategories.length - 1
                    const isOnlyCategory = filteredCategories.length === 1

                    return (
                        <ChakraBreadcrumbItem key={category.id} data-testid="sf-crumb-item">
                            <ChakraBreadcrumbLink
                                as={RouteLink}
                                to={categoryUrlBuilder(category, intl.locale)}
                                sx={{
                                    ...styles.link,
                                    ...((isLast || isOnlyCategory) && styles.lastLink)
                                }}
                            >
                                <Box
                                    sx={{
                                        ...styles.bcText,
                                        ...(isLast && styles.bcTextLast)
                                    }}
                                    as={variant !== 'pdp' && isLast ? 'h1' : 'span'}
                                >
                                    {category.name}
                                </Box>
                            </ChakraBreadcrumbLink>
                        </ChakraBreadcrumbItem>
                    )
                })}
            </ChakraBreadcrumb>
        </>
    )
}

Breadcrumb.displayName = 'Breadcrumb'

Breadcrumb.propTypes = {
    categories: PropTypes.array,
    isPDP: PropTypes.bool,
    variant: PropTypes.string
}

export default Breadcrumb
